import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { workflowAtom } from './atoms';
import { validatedWorkflowFields } from './validatedWorkflowFieldAtoms';
import { validateField } from './formValidation';

/**
 * Hook for workflow form submission
 * Validates all fields and prepares workflow data for API submission
 */
export const useWorkflowFormSubmit = () => {
  const workflow = useAtomValue(workflowAtom);
  const setNameError = useSetAtom(validatedWorkflowFields.name.errorAtom);
  const setDescriptionError = useSetAtom(
    validatedWorkflowFields.description.errorAtom
  );
  const setSlaveError = useSetAtom(validatedWorkflowFields.slave.errorAtom);
  const setThrottleRateError = useSetAtom(
    validatedWorkflowFields.throttleRate.errorAtom
  );
  const setBusinessProductError = useSetAtom(
    validatedWorkflowFields.businessProduct.errorAtom
  );

  const setNameTouched = useSetAtom(validatedWorkflowFields.name.touchedAtom);
  const setDescriptionTouched = useSetAtom(
    validatedWorkflowFields.description.touchedAtom
  );
  const setSlaveTouched = useSetAtom(validatedWorkflowFields.slave.touchedAtom);
  const setThrottleRateTouched = useSetAtom(
    validatedWorkflowFields.throttleRate.touchedAtom
  );
  const setBusinessProductTouched = useSetAtom(
    validatedWorkflowFields.businessProduct.touchedAtom
  );

  const validateAllFields = useCallback(() => {
    let isValid = true;
    const errors: Record<string, string> = {};

    // Validate name
    const nameValidation = validateField(
      workflow.name as any,
      validatedWorkflowFields.name.validator
    );
    setNameTouched(true);
    setNameError(nameValidation.errorMessage);
    if (!nameValidation.isValid) {
      isValid = false;
      errors.name = nameValidation.errorMessage || 'Invalid';
    }

    // Validate description
    const descriptionValidation = validateField(
      workflow.description as any,
      validatedWorkflowFields.description.validator
    );
    setDescriptionTouched(true);
    setDescriptionError(descriptionValidation.errorMessage);
    if (!descriptionValidation.isValid) {
      isValid = false;
      errors.description = descriptionValidation.errorMessage || 'Invalid';
    }

    // Validate slave
    const slaveValidation = validateField(
      workflow.slave as any,
      validatedWorkflowFields.slave.validator
    );
    setSlaveTouched(true);
    setSlaveError(slaveValidation.errorMessage);
    if (!slaveValidation.isValid) {
      isValid = false;
      errors.slave = slaveValidation.errorMessage || 'Invalid';
    }

    // Validate throttle rate
    const throttleRateValidation = validateField(
      workflow.throttleRate as any,
      validatedWorkflowFields.throttleRate.validator
    );
    setThrottleRateTouched(true);
    setThrottleRateError(throttleRateValidation.errorMessage);
    if (!throttleRateValidation.isValid) {
      isValid = false;
      errors.throttleRate = throttleRateValidation.errorMessage || 'Invalid';
    }

    // Validate business product
    const businessProductValidation = validateField(
      workflow.businessProduct as any,
      validatedWorkflowFields.businessProduct.validator
    );
    setBusinessProductTouched(true);
    setBusinessProductError(businessProductValidation.errorMessage);
    if (!businessProductValidation.isValid) {
      isValid = false;
      errors.businessProduct =
        businessProductValidation.errorMessage || 'Invalid';
    }

    return { isValid, errors };
  }, [
    workflow,
    setNameTouched,
    setNameError,
    setDescriptionTouched,
    setDescriptionError,
    setSlaveTouched,
    setSlaveError,
    setThrottleRateTouched,
    setThrottleRateError,
    setBusinessProductTouched,
    setBusinessProductError,
  ]);

  const handleSubmit = useCallback(
    async (
      onSubmitCallback?: (data: typeof workflow) => Promise<void> | void
    ) => {
      const { isValid, errors } = validateAllFields();

      if (!isValid) {
        console.error('Form validation failed:', errors);
        return { success: false, errors };
      }

      try {
        if (onSubmitCallback) {
          await onSubmitCallback(workflow);
        }
        return { success: true, data: workflow };
      } catch (error) {
        console.error('Form submission failed:', error);
        return { success: false, error };
      }
    },
    [validateAllFields, workflow]
  );

  const clearAllErrors = useCallback(() => {
    setNameError(null);
    setDescriptionError(null);
    setSlaveError(null);
    setThrottleRateError(null);
    setBusinessProductError(null);

    setNameTouched(false);
    setDescriptionTouched(false);
    setSlaveTouched(false);
    setThrottleRateTouched(false);
    setBusinessProductTouched(false);
  }, [
    setNameError,
    setDescriptionError,
    setSlaveError,
    setThrottleRateError,
    setBusinessProductError,
    setNameTouched,
    setDescriptionTouched,
    setSlaveTouched,
    setThrottleRateTouched,
    setBusinessProductTouched,
  ]);

  return {
    validateAllFields,
    handleSubmit,
    clearAllErrors,
  };
};
